/**
 * Companion Panels — crisis, professional support, usage, disclaimer, consent.
 *
 * Ported from Profound Thinking's (which came from Profound Living's — the
 * reference coaching client) and re-skinned to this app's slate-and-campfire
 * palette. The crisis and professional cards keep their red and amber
 * families: safety surfaces should look like safety surfaces, not decoration.
 * Amber is this app's accent colour, so the professional-support card is
 * deliberately given the heavier border and the warning icon that carry its
 * meaning where hue alone no longer distinguishes it.
 *
 * Australian English throughout.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Phone, MessageCircle, ExternalLink, Heart, ShieldCheck, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { coachCardLines, textLineSmsHref } from '../../../../../packages/crisis-support/src/textLines.js';

export function CrisisResourcesCard({ resources }) {
  if (!resources?.length) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border-2 border-red-400 bg-red-950/60 p-5 shadow-lg"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="flex-shrink-0 mt-1 text-red-300" size={22} />
        <div className="flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-red-100">
            Please speak to someone right now
          </h3>
          <p className="text-sm sm:text-base text-red-100/90 mt-1 leading-relaxed">
            What you&apos;re carrying deserves real, human support. Please reach out. They&apos;re trained
            for exactly this, and they&apos;re there around the clock.
          </p>
          <ul className="mt-3 space-y-2">
            {coachCardLines(resources).map((r) => (
              <li key={r.name} className="text-sm sm:text-base">
                <span className="font-semibold text-red-100">{r.name}</span>
                <span className="text-red-100/90">: {r.detail}</span>
                {r.phone && (
                  <a
                    href={`tel:${r.phone.replace(/\s/g, '')}`}
                    className="ml-2 inline-flex items-center gap-1 font-bold text-red-200 hover:text-white underline"
                  >
                    <Phone size={14} /> {r.phone}
                  </a>
                )}
                {r.text && (textLineSmsHref(r.text) ? (
                  <a href={textLineSmsHref(r.text)} className="ml-2 inline-flex items-center gap-1 font-bold text-red-200 hover:text-white underline">
                    <MessageCircle size={14} /> {r.text}
                  </a>
                ) : (
                  <span className="ml-2 inline-flex items-center gap-1 font-bold text-red-200">
                    <MessageCircle size={14} /> {r.text}
                  </span>
                ))}
                {r.url && (
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2 inline-flex items-center gap-1 text-red-200 hover:text-white"
                    aria-label={`${r.name} website`}
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}

export function ProfessionalSupportCard({ support }) {
  if (!support) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border-2 border-orange-400/80 bg-orange-950/50 p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <Heart className="flex-shrink-0 mt-1 text-orange-300" size={20} />
        <div className="flex-1">
          <h4 className="font-semibold text-orange-200 text-sm sm:text-base">
            Worth considering: {support.type}
          </h4>
          <p className="text-sm sm:text-base text-orange-100/90 mt-1 leading-relaxed">{support.rationale}</p>
          {support.howToFind?.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm sm:text-base text-orange-100/90 list-disc pl-5">
              {support.howToFind.map((hint, i) => <li key={i}>{hint}</li>)}
            </ul>
          )}
          {support.country && support.country !== 'INTERNATIONAL' && (
            <p className="text-xs text-orange-200/80 mt-2">
              Showing resources for {support.country}.
            </p>
          )}
          <p className="text-xs sm:text-sm text-orange-200/80 mt-3 italic">{support.disclaimer}</p>
        </div>
      </div>
    </motion.div>
  );
}

export function SubtleProfessionalHint({ support }) {
  const [expanded, setExpanded] = useState(false);
  if (!support) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-sky-400/40 bg-slate-950/80 px-4 py-3"
    >
      <div className="flex items-start gap-2">
        <Info className="flex-shrink-0 mt-0.5 text-sky-300" size={16} />
        <div className="flex-1 text-sm sm:text-base text-slate-200 leading-relaxed">
          <p>{support.rationale}</p>
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1.5 flex items-center gap-1 text-xs sm:text-sm font-medium text-sky-300 hover:text-white"
            aria-expanded={expanded}
          >
            How to find one
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {expanded && support.howToFind?.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs sm:text-sm text-slate-200 list-disc pl-4">
              {support.howToFind.map((hint, i) => <li key={i}>{hint}</li>)}
            </ul>
          )}
          <p className="text-xs text-slate-400 mt-2 italic">{support.disclaimer}</p>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Usage bar. `suppressUpgrade` inherits Willow's rule down the family line:
 * any crisis card or amber-tone professional referral on screen kills the
 * upsell for the rest of the exchange. A storytelling workshop is a lighter
 * room than grief — but Module 6 exists precisely because this craft walks
 * people up to raw material, so the rule earns its place here.
 */
export function UsageIndicator({ usage, suppressUpgrade = false }) {
  if (!usage) return null;
  const { tier, used, dailyLimit, remaining, upgradeUrl } = usage;
  const pct = dailyLimit ? Math.min(100, (used / dailyLimit) * 100) : 0;
  const showUpgrade = !suppressUpgrade && tier === 'free' && upgradeUrl && remaining <= 2;

  return (
    <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400">
      <div className="flex-1 max-w-[120px] h-1.5 bg-slate-950 rounded-full overflow-hidden">
        <div
          className={`h-full ${pct >= 80 ? 'bg-orange-400' : 'bg-amber-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span>{used}/{dailyLimit} today</span>
      {showUpgrade && (
        <a href={upgradeUrl} className="ml-2 font-medium text-amber-300 hover:text-white underline">
          More time with Marlow
        </a>
      )}
    </div>
  );
}

/**
 * The standing disclaimer. No hardcoded crisis number (the fleet rule since
 * Sofia's build): a fixed Lifeline line was Australia-only in a seven-market
 * app, and the Hub's safeguards already serve verified, region-correct crisis
 * lines — keyed to the country given at consent — through CrisisResourcesCard
 * the moment a conversation warrants them.
 */
export function CompanionDisclaimer() {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-slate-950/80 border border-white/10 rounded-lg text-xs sm:text-sm text-slate-300 leading-snug">
      <Info className="flex-shrink-0 text-slate-400" size={15} />
      <span>
        Marlow is an AI companion, not a counsellor or therapist. If something with real personal
        weight arrives, <span className="font-medium text-slate-100">the craft gets set down and
        you&apos;ll be pointed to human support</span>.
      </span>
    </div>
  );
}

// The Hub carries region-specific crisis lines for these seven and falls back
// to honest international directories for anything else. "Somewhere else"
// exists so a person in, say, Germany says so explicitly rather than being
// handed a UK number that does not work for them.
const COUNTRIES = [
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'IN', label: 'India' },
  { code: 'UK', label: 'United Kingdom' },
  { code: 'IE', label: 'Ireland' },
  { code: 'CA', label: 'Canada' },
  { code: 'US', label: 'United States' },
  { code: 'OTHER', label: 'Somewhere else' },
];

export function ConsentGate({ onAccept, onDecline }) {
  const [firstName, setFirstName] = useState('');
  const [country, setCountry] = useState('');
  const [style, setStyle] = useState('facilitative');
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFailed(null);
    const result = await onAccept({
      personalContext: {
        firstName: firstName.trim(),
        country: country || null,
        preferredCoachingStyle: style,
        communicationPreference: 'balanced',
        pace: 'moderate',
      },
      // Always app-only — same reasoning as Willow's and Sofia's gates:
      // dataSharingScope gates a cross-app feature that does not exist yet,
      // and asking someone to make a privacy decision with no effect spends
      // trust for nothing.
      dataSharingScope: 'app-only',
    });
    if (result?.success === false) {
      setFailed(result.response || 'That didn’t save. Please try again in a moment.');
    }
    setSubmitting(false);
  };

  const field = 'w-full rounded-lg border border-white/15 bg-slate-950 text-slate-100 px-3 py-2 text-base focus:ring-2 focus:ring-amber-400/70 focus:border-amber-400/70';

  return (
    <div className="max-w-2xl mx-auto bg-slate-900 border border-white/10 rounded-2xl shadow-lg p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="text-amber-400" size={28} />
        <h2 className="text-xl sm:text-2xl font-bold text-white">Before we begin</h2>
      </div>

      <div className="space-y-3 text-sm sm:text-base text-slate-200 leading-relaxed">
        <p>
          Marlow remembers. That&apos;s what makes a rehearsal partner different from a search box:
          the story you were building last week, how the telling actually went when you tried it on
          someone, and the ones you&apos;re still circling. Which means storing a small profile.
        </p>
        <p>
          <strong className="text-white">What&apos;s kept:</strong> what you tell Marlow about
          yourself, full transcripts of your conversations with her and summaries of them, and a
          one-line summary of your Storytelling Style Profile. It&apos;s kept for The Art of Story
          only and doesn&apos;t travel to your other Dive Deep Dialogues apps. You can export or
          delete every bit of it whenever you like, and deleting means gone.
        </p>
        <p>
          <strong className="text-white">What Marlow isn&apos;t:</strong> a therapist, counsellor or
          doctor. Storytelling is not therapy, and the stories worth telling are often about hard
          things. If what you bring needs a professional, Marlow will say so plainly rather than
          quietly carrying on. If you&apos;re still inside an experience rather than looking back at
          it, she&apos;ll say that too: it may not be ready to be a story yet, and that is fine.
        </p>
        <p className="text-slate-400 italic">
          What Marlow remembers is stored in Australia. To write each reply, your message is sent to
          Anthropic&apos;s Claude, which doesn&apos;t use it to train AI. We never sell your data or
          share it with advertisers.
        </p>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="marlow-name" className="block text-sm font-medium text-slate-100 mb-1">
            What should Marlow call you?
          </label>
          <input
            id="marlow-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value.slice(0, 40))}
            placeholder="First name or nickname"
            className={field}
            maxLength={40}
          />
        </div>

        <div>
          <label htmlFor="marlow-country" className="block text-sm font-medium text-slate-100 mb-1">
            Where are you based?{' '}
            <span className="text-slate-400 font-normal">(so any support resources Marlow shows you are ones you can actually ring)</span>
          </label>
          <select id="marlow-country" value={country} onChange={(e) => setCountry(e.target.value)} className={field}>
            <option value="">Select a country</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>

        <div>
          <label htmlFor="marlow-style" className="block text-sm font-medium text-slate-100 mb-1">
            How would you like Marlow to work with you?
          </label>
          {/* Default is facilitative — Marlow's contract is that the story stays
              the teller's, built with them and never for them, so defaulting
              anywhere else would fight the persona from the first message. The
              others adjust the rhythm, not the character. */}
          <select id="marlow-style" value={style} onChange={(e) => setStyle(e.target.value)} className={field}>
            <option value="facilitative">Workshop it with me: questions, and room to answer</option>
            <option value="directive">Teach me the craft: more technique, quicker notes</option>
            <option value="challenging">Push hard: keep asking for the detail I skipped</option>
            <option value="supportive">Gentler pace: I&apos;m nervous about this one</option>
          </select>
        </div>

        {failed && (
          <p className="text-sm sm:text-base text-red-300 bg-red-950/50 border border-red-500/50 rounded-lg px-3 py-2" role="alert">
            {failed}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg font-semibold disabled:opacity-60 transition-colors"
          >
            {submitting ? 'Saving…' : 'I understand. Let’s begin'}
          </button>
          {onDecline && (
            <button type="button" onClick={onDecline} className="px-4 py-3 text-slate-400 hover:text-white font-medium">
              Not just now
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
